// src/components/GrafanaTest.jsx
import React, { useState, useEffect, useRef } from 'react';
import './GrafanaTest.css';
import { useAuth } from '../hooks/useAuth';
 
const GrafanaTest = () => {
  const { user, isLoading } = useAuth();
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDashboard, setSelectedDashboard] = useState('');
  const [embedUrl, setEmbedUrl] = useState('');
  const [generating, setGenerating] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');
  const [iframeKey, setIframeKey] = useState(0);
  const [testParams, setTestParams] = useState({
    dashboard_uid: '',
    panel_id: 1,
    from: 'now-6h',
    to: 'now',
    theme: 'dark',
    refresh: '',
    timezone: ''
  });
 
  const iframeRef = useRef(null);
 
  // Cargar dashboards disponibles
  useEffect(() => {
    fetchDashboards();
  }, []);
  
  // Actualizar URL del iframe cuando cambia el dashboard seleccionado (incl. carga inicial)
  useEffect(() => {
    const apiBase = import.meta.env.VITE_API_URL.replace(/\/+$/, '');
    // No cargar iframe hasta que tengamos dashboards cargados
    if (loading || dashboards.length === 0) {
        return;
    }
   
    if (!selectedDashboard) {
        // Si no hay dashboard seleccionado pero hay dashboards disponibles, seleccionar el primero
        if (dashboards.length > 0) {
            const firstDashboard = dashboards[0];
            setSelectedDashboard(firstDashboard.uid);
            setTestParams(prev => ({
                ...prev,
                dashboard_uid: firstDashboard.uid
            }));
        }
        return;
    }
 
    const dashboard = dashboards.find(d => d.uid === selectedDashboard);
    if (!dashboard) return;
 
    const titleSlug = (dashboard.title || 'dashboard')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-_]/g, '') || 'dashboard';

    // Construimos la URL que entra al grupo /grafanaserve de Echo (igual que Energy.jsx usa el backend)
    const newUrl = `${apiBase}/d/${selectedDashboard}/${titleSlug}?orgId=1&kiosk&theme=${testParams.theme}&from=${testParams.from}&to=${testParams.to}`;
    setIframeUrl(newUrl);
    setIframeKey(prev => prev + 1);
}, [selectedDashboard, dashboards, testParams.theme, testParams.from, testParams.to, loading]);

  const fetchDashboards = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/api/grafanaserve/getDashboards', {
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        }
      });

      // Verificar si la respuesta es JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Expected JSON but got:', contentType, text.substring(0, 200));
        throw new Error(`Server returned ${contentType} instead of JSON. Status: ${response.status}`);
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(`Error ${response.status}: ${errorData.message || response.statusText}`);
      }

      const data = await response.json();
      setDashboards(data.dashboards || data);
     
      // Seleccionar el primer dashboard si existe
      if (data.dashboards?.length > 0) {
        const firstDashboard = data.dashboards[0];
        setSelectedDashboard(firstDashboard.uid);
        setTestParams(prev => ({
          ...prev,
          dashboard_uid: firstDashboard.uid
        }));
      } else if (Array.isArray(data) && data.length > 0) {
        // Si la respuesta es directamente un array
        const firstDashboard = data[0];
        setSelectedDashboard(firstDashboard.uid);
        setTestParams(prev => ({
          ...prev,
          dashboard_uid: firstDashboard.uid
        }));
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching dashboards:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateEmbedUrl = async () => {
    if (!testParams.dashboard_uid) {
      setError('Selecciona un dashboard primero');
      return;
    }
 
    try {
      setGenerating(true);
      setError(null);
 
      const response = await fetch('/grafanaserve/embed', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(testParams)
      });
 
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
 
      const data = await response.json();
      setEmbedUrl(data.proxy_url);
     
      // Construir URL completa para el iframe
      const fullUrl = `${window.location.origin}${data.proxy_url}`;
      setIframeUrl(fullUrl);
      setIframeKey(prev => prev + 1); // Forzar recarga del iframe
     
    } catch (err) {
      setError(err.message);
      console.error('Error generating embed URL:', err);
    } finally {
      setGenerating(false);
    }
  };
 
  const handleDashboardChange = (e) => {
    const uid = e.target.value;
    setSelectedDashboard(uid);
    setTestParams(prev => ({
      ...prev,
      dashboard_uid: uid
    }));
  };
 
  const handleParamChange = (param, value) => {
    setTestParams(prev => ({
      ...prev,
      [param]: value
    }));
  };
 
  const refreshIframe = () => {
    setIframeKey(prev => prev + 1);
  };
 
  const openInNewTab = () => {
    if (iframeUrl) {
      window.open(iframeUrl, '_blank');
    }
  };
 
  const copyEmbedUrl = async () => {
    if (embedUrl) {
      try {
        await navigator.clipboard.writeText(embedUrl);
        alert('URL copiada al portapapeles');
      } catch (err) {
        console.error('Error copying URL:', err);
      }
    }
  };
 
  const quickTimeRange = (range) => {
    let from, to;
    switch(range) {
      case '1h':
        from = 'now-1h';
        to = 'now';
        break;
      case '6h':
        from = 'now-6h';
        to = 'now';
        break;
      case '24h':
        from = 'now-24h';
        to = 'now';
        break;
      case '7d':
        from = 'now-7d';
        to = 'now';
        break;
      case '30d':
        from = 'now-30d';
        to = 'now';
        break;
      default:
        return;
    }
   
    setTestParams(prev => ({
      ...prev,
      from,
      to
    }));
  };
 
  if (isLoading) {
    return <div className="loading">Cargando autenticación...</div>;
  }
 
  const selectedDashboardInfo = dashboards.find(d => d.uid === selectedDashboard);
 
  return (
    <div className="grafana-test-container">
      <header className="test-header">
        <h1>📊 Prueba de Dashboards Grafana</h1>
        <div className="user-info">
          <span>Usuario: {user?.name || user?.email || 'N/A'}</span>
          <button onClick={fetchDashboards} className="refresh-btn">
            🔄 Actualizar Dashboards
          </button>
        </div>
      </header>
 
      <div className="test-content">
        {/* Panel izquierdo: Configuración */}
        <div className="config-panel">
          <div className="config-section">
            <h2>Dashboard</h2>
           
            {loading ? (
              <div className="loading">Cargando dashboards...</div>
            ) : error ? (
              <div className="error-message">
                <p>❌ Error: {error}</p>
                <button onClick={fetchDashboards}>Reintentar</button>
              </div>
            ) : dashboards.length === 0 ? (
              <div className="no-data">No hay dashboards disponibles</div>
            ) : (
              <>
                <div className="form-group">
                  <label htmlFor="dashboard-select">Seleccionar Dashboard:</label>
                  <select
                    id="dashboard-select"
                    value={selectedDashboard}
                    onChange={handleDashboardChange}
                    className="dashboard-select"
                  >
                    <option value="">-- Selecciona un dashboard --</option>
                    {dashboards.map(dashboard => (
                      <option key={dashboard.uid} value={dashboard.uid}>
                        {dashboard.title} ({dashboard.uid})
                      </option>
                    ))}
                  </select>
                </div>
 
                {selectedDashboardInfo && (
                  <div className="dashboard-info">
                    <h3>{selectedDashboardInfo.title}</h3>
                    <p><strong>UID:</strong> {selectedDashboardInfo.uid}</p>
                  </div>
                )}
              </>
            )}
          </div>
 
          <div className="config-section">
            <h2>Parámetros de Visualización</h2>
           
            <div className="form-group">
              <label htmlFor="panel-id">Panel ID:</label>
              <input
                id="panel-id"
                type="number"
                value={testParams.panel_id}
                onChange={(e) => handleParamChange('panel_id', parseInt(e.target.value) || 1)}
                min="1"
                step="1"
              />
            </div>
 
            <div className="form-group">
              <label>Rango de tiempo rápido:</label>
              <div className="quick-buttons">
                <button onClick={() => quickTimeRange('1h')}>1 hora</button>
                <button onClick={() => quickTimeRange('6h')}>6 horas</button>
                <button onClick={() => quickTimeRange('24h')}>24 horas</button>
                <button onClick={() => quickTimeRange('7d')}>7 días</button>
                <button onClick={() => quickTimeRange('30d')}>30 días</button>
              </div>
            </div>
 
            <div className="form-group">
              <label htmlFor="from">Desde:</label>
              <input
                id="from"
                type="text"
                value={testParams.from}
                onChange={(e) => handleParamChange('from', e.target.value)}
                placeholder="Ej: now-6h"
              />
            </div>
 
            <div className="form-group">
              <label htmlFor="to">Hasta:</label>
              <input
                id="to"
                type="text"
                value={testParams.to}
                onChange={(e) => handleParamChange('to', e.target.value)}
                placeholder="Ej: now"
              />
            </div>
 
            <div className="form-group">
              <label htmlFor="theme">Tema:</label>
              <select
                id="theme"
                value={testParams.theme}
                onChange={(e) => handleParamChange('theme', e.target.value)}
              >
                <option value="dark">Oscuro</option>
                <option value="light">Claro</option>
                <option value="system">Preferencia del sistema</option>
                <option value="desertbloom">Floración del desierto</option>
                <option value="gildedgrove">Arboleda dorada</option>
                <option value="gloom">Tristeza</option>
                <option value="sapphiredusk">Atardecer de zafiro</option>
                <option value="tron">Tron</option>
              </select>
            </div>
 
            <div className="form-group">
              <label htmlFor="refresh">Auto-refresh:</label>
              <input
                id="refresh"
                type="text"
                value={testParams.refresh}
                onChange={(e) => handleParamChange('refresh', e.target.value)}
                placeholder="Ej: 30s, 1m, 5m"
              />
            </div>
 
            <div className="form-group">
              <label htmlFor="timezone">Zona horaria:</label>
              <input
                id="timezone"
                type="text"
                value={testParams.timezone}
                onChange={(e) => handleParamChange('timezone', e.target.value)}
                placeholder="Ej: Europe/Madrid, America/New_York"
              />
            </div>
 
          </div>
 
          {embedUrl && (
            <div className="config-section">
              <h2>URL Generada</h2>
              <div className="url-display">
                <code>{embedUrl}</code>
                <div className="url-actions">
                  <button onClick={copyEmbedUrl} className="copy-btn">
                    📋 Copiar
                  </button>
                  <button onClick={openInNewTab} className="new-tab-btn">
                    ↗ Abrir en nueva pestaña
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
 
        {/* Panel derecho: Vista previa */}
        <div className="preview-panel">
          <div className="preview-header">
            <h2>Vista Previa</h2>
            <div className="preview-controls">
              <button onClick={refreshIframe} className="refresh-iframe-btn">
                🔄 Actualizar
              </button>
              <button onClick={openInNewTab} className="open-new-btn">
                ↗ Pantalla completa
              </button>
            </div>
          </div>
 
          {iframeUrl ? (
            <div className="iframe-container">
              <iframe
                key={iframeKey}
                ref={iframeRef}
                src={iframeUrl}
                title="Grafana Dashboard Preview"
                className="grafana-iframe"
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                allow="fullscreen"
              />
            </div>
          ) : (
            <div className="no-preview">
              <div className="placeholder">
                <div className="placeholder-icon">📊</div>
                <h3>Previsualización de Dashboard</h3>
                <p>Genera una URL de embed para ver el dashboard aquí</p>
                <button onClick={generateEmbedUrl} className="placeholder-btn">
                  Generar mi primer embed
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
 
export default GrafanaTest;